import gql from 'graphql-tag';
import React from 'react';
import I18n from 'i18n-js';
import { ChildProps, DataValue } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import { CoreListingRecommendedShopsRow, RecommendedShopsRowListingFields } from '../schemas/rql';
import ShopCard, { COMPONENT_NAME, ShopCardFragment } from './shop_card';
import { SHOP_PAGE } from './shared/url_helpers';

import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { Tiles } from '@reverbdotcom/commons';
import { ClickedRecommendedShopCardEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { connect } from '@reverbdotcom/commons/src/connect';

interface IExternalProps {
  listing: RecommendedShopsRowListingFields.Fragment;
}

type IProps = ChildProps<IExternalProps & IUserContext, CoreListingRecommendedShopsRow.Query, CoreListingRecommendedShopsRow.Variables>;

type Shop = CoreListingRecommendedShopsRow.Shops;
type Listing = RecommendedShopsRowListingFields.Fragment;

export function RecommendedShopsRow(props: IProps) {
  const shops = useShops(props.data);
  const title = useRowTitle();
  const { listing } = props;

  if (!shops.length) {
    return null;
  }

  return (
    <OverflowingRow
      title={title}
      collectionCount={shops.length}
    >
      <Tiles singleRow>
        {shops.map((shop, index) => (
          <CoreLink
            key={shop.id}
            to={shopLink(shop, listing)}
            clickEvent={buildClickEvent({
              shop,
              listing,
              position: index,
              shopCount: shops.length,
            })}
          >
            <ShopCard shop={shop} />
          </CoreLink>
        ))}
      </Tiles>
    </OverflowingRow>
  );
}

function optionalCategory(listing: Listing) {
  const category = listing.categories[0];
  if (category?.slug) {
    return { category: category.slug };
  }
  return {};
}

function shopLink(shop: Shop, listing: Listing) {
  return SHOP_PAGE.expand({
    slug: shop.slug,
    query: {
      ...optionalCategory(listing),
      make: listing.brandSlug,
    },
  });
}

function useShops(data: DataValue<CoreListingRecommendedShopsRow.Query, CoreListingRecommendedShopsRow.Variables>) {
  if (data.loading || !data.listingsAggregationSearch) {
    return [];
  }

  return data.listingsAggregationSearch.shops;
}

function useRowTitle() {
  const user = useUser();

  if (user.countryCode === 'GB') {
    return I18n.t('discovery.shopCard.recommendedShopsTitle.gb');
  }

  return I18n.t('discovery.shopCard.recommendedShopsTitle.default');
}

function buildClickEvent({
  shop,
  listing,
  position,
  shopCount,
}: {
  shop: Shop,
  listing: Listing,
  position: number,
  shopCount: number,
}): ClickedRecommendedShopCardEvent {
  return {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedRecommendedShopCard,
    listingId: listing.id,
    position,
    shopCount,
    shopId: shop.id,
  };
}

export const RecommendedShopsRowListingFragment = gql`
  fragment RecommendedShopsRowListingFields on Listing {
    _id
    id
    brandSlug
    categories {
      _id
      slug
    }
  }
`;

const withQuery = withGraphql<
IExternalProps & IUserContext,
CoreListingRecommendedShopsRow.Query,
CoreListingRecommendedShopsRow.Variables
>(
  gql`
    query Core_Listing_RecommendedShopsRow(
      $itemRegion: String,
      $brandSlugs: [String],
      $categorySlugs: [String]
    ) {
      listingsAggregationSearch(input: {
        withAggregations: [SHOP_ID],
        preferredSeller: true,
        brandSlugs: $brandSlugs,
        categorySlugs: $categorySlugs,
        itemRegion: $itemRegion
      }) {
        shops {
          _id
          id
          slug
          address {
            countryCode
          }
          ...ShopCardFields
        }
      }
    }
    ${ShopCardFragment}
  `,
  {
    options: ({ listing, user }: IProps) => ({
      ssr: true,
      variables: {
        brandSlugs: listing.brandSlug ? [listing.brandSlug] : [],
        categorySlugs: listing.categories.map(category => category.slug),
        itemRegion: user.countryCode ? user.countryCode : undefined,
      },
    }),
  },
);

export default connect<IExternalProps>([
  withUserContext,
  withQuery,
])(RecommendedShopsRow);
